import React from 'react';

import {
	defaultSorter
} from '../helper/Brood.js';

import {
	broncoURLENV
} from '../ENV';

// plenty for any realistic buffer/limit, and keeps the set from growing for the
// life of a kiosk that never reloads
const SPENT_SLIDE_ID_LIMIT = 200;

class PeacockProviderComponent extends React.Component {
	static providerName = 'peacock';

	constructor(props) {
		super(props);

		this.state = {
			slides:             [],
			lastProofOfPlayURL: '',
			// -1 because we calculate from the 0 index
			split:              (props.limit - 1),

			adsCount: 0,
			popCount: 0
		};

		this.providerID = `PROVIDER_${props.component_name}`;

		// slideIDs whose lease has been proof-of-played. A spent lease must never
		// be re-admitted to the buffer — it would play twice and the ad server
		// answers its second POP with 400 Lease Already Spent, so the replay is
		// unbillable. Bounded because leases expire anyway.
		this.spentSlideIDs = new Set();
	}

	markSlideSpent(slideID) {
		if (!slideID) {
			return;
		}

		this.spentSlideIDs.add(slideID);

		// Sets iterate in insertion order, so this drops the oldest first
		while (this.spentSlideIDs.size > SPENT_SLIDE_ID_LIMIT) {
			this.spentSlideIDs.delete(this.spentSlideIDs.values().next().value);
		}
	}

	componentDidMount() {
		const {
			updateSlideshowProvider
			// limit
		} = this.props;

		// don't initialize if we're not in a slideshow
		if (!updateSlideshowProvider) {
			return;
		}

		// Register an empty provider at mount so Brood/VCL know Peacock's slots
		// exist even if the first (or every) fetch fails. Without this, a
		// network-blocked Peacock never registers → backfill can't fill the slots
		// and Peacock is missing from the debug overlay.
		updateSlideshowProvider(this.makeProvider([]));

		this.abortController = new AbortController();
		this.fetchSlides();
		this.updateInterval = setInterval(() => {
			this.fetchSlides();
		}, 1000 * 60);
	}

	componentWillUnmount() {
		if (this.abortController) {
			this.abortController.abort();
		}
		clearInterval(this.updateInterval);
	}

	componentDidUpdate(prevProps) {
		const {
			last_update,
			provider
		} = this.props;

		if (prevProps.last_update !== last_update || prevProps.provider !== provider) {
			this.fetchSlides();
		}
	}

	makeRequestBody() {
		const {
			peacock_size,
			dmn_operator,
			peacock_media_type,
			peacock_allow_audio: allow_audio,
			peacock_static_duration,
			peacock_duration_min,
			peacock_duration_max,
			peacock_max_file_size_bytes,
			limit,
			venue_id
		} = this.props;

		const [
			width,
			height
		] = (peacock_size || '0x0')
			.split('x')
			.map((n) => {
				return Number(n);
			});

		return {
			allow_audio:         allow_audio, // wether audio is allowed or not
			height:              height, // the height of the screen section
			max_duration:        peacock_duration_max, // the maximum duration of video assets
			max_file_size_bytes: peacock_max_file_size_bytes, // maximum file size in bytes
			min_duration:        peacock_duration_min, // the minumum duration of video assets
			provider:            dmn_operator,
			static_duration:     peacock_static_duration, // the duration of static assets
			supported_media:     (peacock_media_type || '').split(','),
			venue_id:            venue_id, // highly recommended
			width:               width, // the width of the screen section

			limit: limit
		};
	}


	async fetchSlides() {
		if (this.fetching) {
			return;
		}

		this.fetching = true;

		const {
			limit,
			updateSlideshowProvider,
			peacock_allow_audio: allow_audio
		} = this.props;

		const {
			slides
		} = this.state;

		const nonExpiredSlides = slides.filter((slide) => {
			if (this.expired(slide)) {
				return false;
			}

			return true;
		});

		if (nonExpiredSlides.length >= limit) {
			this.fetching = false;

			return;
		}

		// captured so an aborted request can't release the guard belonging to a
		// later request that replaced its controller
		const abortController = this.abortController;

		const newSlides = [];
		try {
			// inside the try so a malformed/missing setting can't reject
			// fetchSlides with this.fetching stuck true — that would block the
			// 60s interval and every componentDidUpdate refetch for good
			const body = this.makeRequestBody();

			// try to always have a buffer of #limit of slides
			if (body.limit > nonExpiredSlides.length) {
				body.limit = (limit - nonExpiredSlides.length);
			}

			const fetchURL = `${broncoURLENV}/peacock?tlc=${window._getEnv('CONDOR_COMPONENT')}`;
			const response = await fetch(fetchURL, {
				body:    JSON.stringify(body),
				headers: {
					'Content-Type': 'application/json'
				},
				method: 'POST',
				signal: abortController?.signal
			});

			const data = await response.json();

			const ads = [];
			for (const ad of data.ads) {
				ads.push(ad);
			}

			for (const slide of ads) {
				slide.data = {
					...slide,
					src: slide.asset_url,
					alt: slide.description,
					convertFormat: false
				};

				slide.options = {
					duration: (Number(slide.length_in_seconds) * 1000)
				};

				// slide.proof_of_play_url is unique
				slide.slideID = `${btoa(slide.proof_of_play_url)}`;
				slide.type = slide.mime_type.split('/')[0];
				slide.allowAudio = allow_audio;

				slide.provider = this;
				newSlides.push(slide);
			}

			const lengthBeforeMerge = this.state.slides.length;

			// Merge against CURRENT state, not the snapshot taken before the
			// await. An ad can finish playing (and be proof-of-played and
			// removed by afterNext) while this request is in flight; merging into
			// the stale snapshot would put that spent slide back in the buffer,
			// where it plays a second time and re-POPs a lease the ad server has
			// already marked spent.
			this.setState((prevState) => {
				const currentSlides = prevState.slides.filter((slide) => {
					return !this.expired(slide);
				});

				const existingSlideIDs = currentSlides.map((slide) => {
					return slide.slideID;
				});

				const mergedSlides = [...currentSlides];
				let added = 0;
				for (let i = 0; i < newSlides.length; i++) {
					const newSlide = newSlides[i];

					// never re-admit a lease we've already proof-of-played
					if (this.spentSlideIDs.has(newSlide.slideID)) {
						continue;
					}

					if (existingSlideIDs.includes(newSlide.slideID)) {
						// same ad, but things like proof_of_play_url might have changed
						mergedSlides[existingSlideIDs.indexOf(newSlide.slideID)] = newSlides[i];
						continue;
					}

					mergedSlides.push(newSlide);
					added++;
				}
				mergedSlides.sort(defaultSorter);

				if (mergedSlides.length > 0 && mergedSlides[0].proof_of_play_url === prevState.lastProofOfPlayURL) {
					// debugger;
					mergedSlides.push(mergedSlides.shift());
				}

				return {
					slides: mergedSlides,
					// -1 because we calculate from the 0 index
					split:    (limit - 1),
					adsCount: (prevState.adsCount + added)
				};
			}, () => {
				// a fetch that returned nothing new (no fill available) would
				// otherwise republish an identical provider, costing Brood a
				// setState + makeSlides + VCL missing-count recompute per ad play
				// (still publish when slides were dropped as expired — that
				// changes the buffer even with nothing new to add)
				if (newSlides.length === 0 && this.state.slides.length === lengthBeforeMerge) {
					return;
				}

				const newProvider = this.makeProvider(this.state.slides);
				updateSlideshowProvider(newProvider);
			});
		} catch (err) {
			if (err.name === 'AbortError') {
				if (abortController === this.abortController) {
					this.fetching = false;
				}

				return;
			}
			console.error('BROOD::PEACOCK::ERROR_fetchSlides:', err);
		}

		this.fetching = false;
	}

	makeProvider(slides = []) {
		const {
			provider = {},
			providerID,
			limit: maxSlides,
			order: providerOrder,
			sort: sortType,
			transition_in: transitionIn,
			transition_out: transitionOut,
			isTouch
		} = this.props;

		// Allow autoplay in chrome
		// let isMuted = ~window.location.href.indexOf('muted=1');

		return {
			providerID:   providerID,
			providerName: this.constructor.providerName, // Add the static providerName to the provider object
			providerType: 'ads',
			options:      {
				maxSlides:     maxSlides,
				providerOrder: providerOrder,
				sortType:      sortType || 'sequential'
			},
			is_backfill:         provider.is_backfill,
			slots:               provider.slots,
			order:               provider.order,
			skipper:             this.skipper.bind(this),
			afterLastSlide:      this.afterLastSlide.bind(this),
			afterNext:           this.afterNext.bind(this),
			providerProofOfPlay: this.providerProofOfPlay.bind(this),
			slides:              slides
		};
	}

	expired(currSlide) {
		const now = new Date();
		const expiresAt = new Date(currSlide.data.lease_expiry * 1000);

		return (now > expiresAt);
	}

	skipper(currSlide) {
		const {
			slides
		} = this.state;

		// A spent lease must never play again, even if it's somehow still in the
		// buffer. Brood POPs a slide immediately but defers afterNext (which
		// removes it) by transitionDuration on this.transitionTimer — and that
		// timer gets cleared both by FORCE_UNLOCK and by the next transition
		// starting inside that window. Either cancels the removal after the POP
		// has already fired, leaving a spent slide in the buffer that would
		// otherwise replay and re-POP for 400 Lease Already Spent.
		if (this.spentSlideIDs.has(currSlide.slideID)) {
			return true;
		}

		// currSlide is the brood slide, not the peacock slide
		const statefulCurrSlide = slides.find((slide) => {
			return slide.slideID === currSlide.slideID;
		});
		if (!statefulCurrSlide) {
			return true;
		}

		return false;
	}

	afterLastSlide(slide) {
		console.log('BROOD::PEACOCK::afterLastSlide:', slide);

		const {
			updateSlideshowProvider
		} = this.props;

		const {
			slides
		} = this.state;

		const newProvider = this.makeProvider(slides);
		updateSlideshowProvider(newProvider);
	}

	afterNext(prevSlides, prevSlide, currSlide) {
		console.log('BROOD::PEACOCK::afterNext:', prevSlides, prevSlide, currSlide);

		const {
			lastProofOfPlayURL
		} = this.state;

		// this will always trip because the pop is callled before the
		// afterNext is called due to the transition duration I believe?
		if (lastProofOfPlayURL === prevSlide.proof_of_play_url) {
			// debugger;
		}

		const {
			updateSlideshowProvider
		} = this.props;

		const {
			slides
		} = this.state;

		// playing a slide is what spends its lease — buffer membership is
		// incidental (it may already have been dropped as expired)
		this.markSlideSpent(prevSlide.slideID);

		let prevSlideIndex = slides.findIndex((slide) => {
			return slide.slideID === prevSlide.slideID;
		});
		if (~prevSlideIndex) {
			// Functional updater against prevState, and a new array rather than a
			// splice: a fetchSlides merge can commit between the read above and
			// this write (React batches), and a plain object write would drop the
			// ads it just added — the mirror image of the resurrection bug.
			this.setState((prevState) => {
				return {
					slides: prevState.slides.filter((slide) => {
						return slide.slideID !== prevSlide.slideID;
					})
				};
			}, () => {
				const newProvider = this.makeProvider(this.state.slides);
				updateSlideshowProvider(newProvider);

				// each time we remove an ad we should try to fetch ads up to our
				// limit again — and publish the result. Brood derives its slide
				// list from provider.slides, so withholding the refill left the
				// provider permanently one slide short of `limit` (the next
				// afterNext removes another before the refill is ever seen), and
				// VCL backfilled the phantom empty slot.
				this.fetchSlides();
			});
		}
	}

	async providerProofOfPlay(slide) {
		let host = window._getEnv('BROOD_POP_URL');
		if (!host) {
			host = window._getEnv('BRONCO_URL');
		}

		const fetchURL = `${host}/peacock_pop?tlc=${window._getEnv('CONDOR_COMPONENT')}`;

		const body = {
			slide_id:          slide.slideID,
			proof_of_play_url: slide.proof_of_play_url,
			display_time:      `${Math.round(Date.now() / 1000)}`
		};

		return fetch(fetchURL, {
			body:    JSON.stringify(body),
			headers: {'Content-Type': 'application/json'},
			method:  'POST'
		})
			.then((res) => {
				if (res.status !== 204) {
					if (res.status !== 200) {
						console.error('BROOD::PEACOCK::ERROR_afterNext:');
						console.error(`providerProofOfPlay() failed with status: ${res.status}`);
					}
				}

				// 200 = success / 400 = Lease Already Spent
				if (!(res.status === 200 || res.status === 400)) {
					throw new Error('Unexpected Proof of Play response');
				}

				// either way the lease is now spent and must not be re-admitted
				this.markSlideSpent(slide.slideID);

				return res.json();
			})
			.then((data) => {
				console.log('BROOD::PEACOCK::PROOF_OF_PLAY_RES:', JSON.stringify(data), slide.slideID);

				this.setState({
					lastProofOfPlayURL: slide.proof_of_play_url,
					popCount:           this.state.popCount + 1
				}, () => {
					console.log('BROOD::PEACOCK::COUNTS:', this.state.adsCount, this.state.popCount);
				});

				// important for chaining in Brood — the structured metrics body
				// (pop_impressions, pop_media_cost, ...) is relayed into the
				// /brood/pop record by Brood.sendProofOfPlay (DEV-11958)
				return data || true;
			})
			.catch((err) => {
				console.error('BROOD::PEACOCK::ERROR_fetchProofOfPlayURL:', err);
			});
	}

	render() {
		return null;
	}
}

export default PeacockProviderComponent;